import { useEffect, useState } from 'react'
import type { SamplingEvent } from '../../utils/samplingEventMath'
import { computeMeanWeightG } from '../../utils/samplingEventMath'
import { formatStoredDateTimeAsLocal } from '../../utils/dateNormalization'
import { weightSampleLogApi } from '../../utils/weightSampleLogApi'
import type { WeightSampleLog } from '../../types/production'
import { useT } from '../../i18n'
import { CrewCorrectionButton } from '../crewCorrection/CrewCorrectionButton'
import { CrewRowBadges, useCorrectionCounts } from '../crewCorrection/CrewRowBadges'
import { useActorNames } from '../../utils/actorNames'
import { collectSamplingEventsForRow } from './weightSampleLogBridge'

/**
 * v2.1 finding #35 — Sampling drill-down popover.
 *
 * When multiple sampling readings land on the same `(lot, day)` the visible
 * production row aggregates them via arithmetic mean (see `saveLogDraft` in
 * [src/screens/Biomass.tsx](../Biomass.tsx)). This popover surfaces the
 * underlying per-event weights and timestamps so operators can audit the
 * displayed average — mirroring the MORT-12 `(from N events)` affordance.
 *
 * v2.1 finding #60 (WSL-05 read cutover, 2026-07-01): source of truth is the
 * server-backed `WeightSampleLog` table via `weightSampleLogApi.list`.
 * v2.1 finding #60 (WSL-08 sidecar retire, 2026-07-01): the `fallbackEvents`
 * bridge prop was removed once the localStorage sidecar was deleted from the
 * bootstrap path — there is no other event source now. See
 * [docs/weight-sample-log-implementation-plan.md](../../../docs/weight-sample-log-implementation-plan.md).
 */
export interface SamplingEventsPopoverProps {
  productionRowId: string
  farmId?: string
  productionUnitId?: string
  lotLabel: string
  dateLabel: string
  onClose: () => void
  /** Crew Mode supervisor correction v0 — called after a per-event correction is saved. */
  onCorrectedEvent?: () => Promise<void> | void
}

const formatEventTime = (recordedAt: string): string => {
  if (!recordedAt) return ''
  const parsed = new Date(recordedAt)
  if (Number.isNaN(parsed.getTime())) return recordedAt
  try {
    return parsed.toLocaleTimeString(undefined, { hour: '2-digit', minute: '2-digit' })
  } catch {
    return parsed.toISOString().slice(11, 16)
  }
}

const formatHeaderDate = (rawDate: string): string => {
  // v2.1 finding #49: honor the calendar-day sentinel (`YYYY-MM-DDT00:00:00Z`)
  // so the header day matches the Database row's `<input type="datetime-local">`
  // display instead of TZ-shifting to the previous day in negative-UTC zones.
  const formatted = formatStoredDateTimeAsLocal(rawDate)
  return formatted || rawDate
}

const sortEventsByTime = (events: SamplingEvent[]): SamplingEvent[] => {
  return [...events].sort((a, b) => {
    const aTime = new Date(a.recordedAt).getTime()
    const bTime = new Date(b.recordedAt).getTime()
    const aValid = !Number.isNaN(aTime)
    const bValid = !Number.isNaN(bTime)
    if (aValid && bValid) return aTime - bTime
    if (aValid) return -1
    if (bValid) return 1
    return 0
  })
}

const DAY_MS = 24 * 60 * 60 * 1000

/**
 * A ±1-day `recordedAt` window around the row's date, wide enough for any
 * timezone offset; `collectSamplingEventsForRow` then keeps only the logs on
 * the row's own calendar day. Null when the row date doesn't parse.
 */
const buildRowDayWindow = (rawDate: string): { fromRecordedAt: string; toRecordedAt: string } | null => {
  const parsed = new Date(rawDate)
  if (Number.isNaN(parsed.getTime())) return null
  return {
    fromRecordedAt: new Date(parsed.getTime() - DAY_MS).toISOString(),
    toRecordedAt: new Date(parsed.getTime() + 2 * DAY_MS).toISOString()
  }
}

const formatLength = (lengthMm: unknown): string => {
  const value = Number(lengthMm)
  if (lengthMm === null || lengthMm === undefined || lengthMm === '' || !Number.isFinite(value)) return '—'
  return `${Number(value.toFixed(1)).toLocaleString()} mm`
}

const formatWeight = (weightG: number): string => {
  if (!Number.isFinite(weightG)) return '—'
  return Number(weightG.toFixed(3)).toLocaleString()
}

export const samplingEventsPopoverHelpers = {
  formatEventTime,
  formatHeaderDate,
  sortEventsByTime,
  formatWeight,
  formatLength,
  buildRowDayWindow
}

export const SamplingEventsPopover = ({
  productionRowId,
  farmId,
  productionUnitId,
  lotLabel,
  dateLabel,
  onClose,
  onCorrectedEvent
}: SamplingEventsPopoverProps) => {
  const t = useT()
  const formattedDate = formatHeaderDate(dateLabel)
  const [serverRows, setServerRows] = useState<WeightSampleLog[] | null>(null)
  const [refreshKey, setRefreshKey] = useState(0)

  useEffect(() => {
    let cancelled = false
    void (async () => {
      try {
        const dayWindow = buildRowDayWindow(dateLabel)
        const [linkedByRowId, unitLogs] = await Promise.all([
          weightSampleLogApi.list({ productionRowId, farmId, productionUnitId }),
          dayWindow && farmId && productionUnitId
            ? weightSampleLogApi.list({ farmId, productionUnitId, ...dayWindow })
            : Promise.resolve([] as WeightSampleLog[])
        ])
        if (cancelled) return
        setServerRows(collectSamplingEventsForRow(
          { id: productionRowId, date: dateLabel, lot: lotLabel },
          linkedByRowId,
          unitLogs
        ))
      } catch {
        if (!cancelled) setServerRows([])
      }
    })()
    return () => {
      cancelled = true
    }
  }, [productionRowId, farmId, productionUnitId, dateLabel, lotLabel, refreshKey])

  // v2.1 finding #60 (WSL-08): server-backed events are the ONLY source.
  // Empty array → popover shows the "no individual samplings recorded" empty
  // state. Previously this fell back to sidecar data for pre-WSL-04 rows;
  // that bridge retired with the sidecar in WSL-08.
  const rows = serverRows ?? []
  const events: SamplingEvent[] = rows.map((row) => ({
    recordedAt: row.recordedAt,
    weightG: Number(row.weightGrams) || 0
  }))
  const sortedRows = [...rows].sort((a, b) => {
    const at = new Date(a.recordedAt).getTime()
    const bt = new Date(b.recordedAt).getTime()
    if (!Number.isNaN(at) && !Number.isNaN(bt)) return at - bt
    if (!Number.isNaN(at)) return -1
    if (!Number.isNaN(bt)) return 1
    return 0
  })
  const sorted = sortEventsByTime(events)
  const mean = computeMeanWeightG(events)
  const hasLength = rows.some((row) => formatLength(row.lengthMm) !== '—')
  // findings #55: Crew / Corregido badges and who logged each sample.
  const actorNames = useActorNames()
  const correctionCounts = useCorrectionCounts('weight-sample-log', rows.map((row) => row.id), refreshKey)

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose()
    }
    globalThis.addEventListener('keydown', onKey)
    return () => globalThis.removeEventListener('keydown', onKey)
  }, [onClose])

  return (
    <div
      data-testid="sampling-events-popover"
      role="dialog"
      aria-modal="true"
      aria-label={t('datatable.sampling.events.popover.title', { lot: lotLabel, date: formattedDate })}
      onClick={onClose}
      style={{
        position: 'fixed',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        backgroundColor: 'rgba(15, 23, 42, 0.55)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        zIndex: 1100,
        padding: 16
      }}
    >
      <div
        onClick={(e) => e.stopPropagation()}
        style={{
          backgroundColor: '#ffffff',
          borderRadius: 12,
          minWidth: 320,
          maxWidth: 520,
          width: '100%',
          maxHeight: '80vh',
          overflow: 'hidden',
          display: 'flex',
          flexDirection: 'column',
          boxShadow: '0 20px 40px rgba(15, 23, 42, 0.25)'
        }}
      >
        <header
          style={{
            padding: '16px 20px',
            borderBottom: '1px solid #e2e8f0',
            display: 'flex',
            alignItems: 'flex-start',
            justifyContent: 'space-between',
            gap: 12
          }}
        >
          <div>
            <h3 style={{ margin: 0, fontSize: 16, color: '#0f172a' }}>
              {t('datatable.sampling.events.popover.title', { lot: lotLabel, date: formattedDate })}
            </h3>
            <p style={{ margin: '4px 0 0', fontSize: 13, color: '#475569' }}>
              {t('datatable.sampling.events.popover.total', {
                count: String(events.length),
                mean: mean === null ? '—' : formatWeight(mean)
              })}
            </p>
          </div>
          <button
            type="button"
            onClick={onClose}
            aria-label={t('datatable.sampling.events.popover.close')}
            style={{
              background: 'transparent',
              border: 'none',
              fontSize: 20,
              color: '#475569',
              cursor: 'pointer',
              lineHeight: 1
            }}
          >
            ✕
          </button>
        </header>
        <div style={{ overflowY: 'auto', padding: '12px 20px 20px' }}>
          {sorted.length === 0 ? (
            <p style={{ margin: 0, fontSize: 13, color: '#64748b' }}>
              {t('datatable.sampling.events.popover.empty')}
            </p>
          ) : (
            <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
              <thead>
                <tr style={{ textAlign: 'left', color: '#475569' }}>
                  <th style={{ padding: '6px 4px', borderBottom: '1px solid #e2e8f0', fontWeight: 600 }}>
                    {t('datatable.sampling.events.popover.column.time')}
                  </th>
                  <th style={{ padding: '6px 4px', borderBottom: '1px solid #e2e8f0', fontWeight: 600, textAlign: 'right' }}>
                    {t('datatable.sampling.events.popover.column.weight')}
                  </th>
                  {hasLength ? (
                    <th style={{ padding: '6px 4px', borderBottom: '1px solid #e2e8f0', fontWeight: 600, textAlign: 'right' }}>
                      {t('datatable.sampling.events.popover.column.length')}
                    </th>
                  ) : null}
                  {onCorrectedEvent ? (
                    <th style={{ padding: '6px 4px', borderBottom: '1px solid #e2e8f0', fontWeight: 600, textAlign: 'right' }} />
                  ) : null}
                </tr>
              </thead>
              <tbody>
                {sortedRows.map((row) => (
                  <tr key={row.id}>
                    <td style={{ padding: '8px 4px', borderBottom: '1px solid #f1f5f9', color: '#0f172a', whiteSpace: 'nowrap' }}>
                      {formatEventTime(row.recordedAt)}
                      <CrewRowBadges
                        captureSource={row.captureSource}
                        capturedByActorId={row.capturedByActorId}
                        correctionCount={correctionCounts.get(row.id) ?? 0}
                        actorNames={actorNames}
                      />
                    </td>
                    <td style={{ padding: '8px 4px', borderBottom: '1px solid #f1f5f9', color: '#0f172a', textAlign: 'right', fontWeight: 600 }}>
                      {formatWeight(Number(row.weightGrams) || 0)} g
                    </td>
                    {hasLength ? (
                      <td style={{ padding: '8px 4px', borderBottom: '1px solid #f1f5f9', color: '#0f172a', textAlign: 'right' }}>
                        {formatLength(row.lengthMm)}
                      </td>
                    ) : null}
                    {onCorrectedEvent ? (
                      <td style={{ padding: '8px 4px', borderBottom: '1px solid #f1f5f9', textAlign: 'right' }}>
                        <CrewCorrectionButton
                          resource="weight-sample-log"
                          entityId={row.id}
                          farmId={farmId}
                          onDone={async () => {
                            setRefreshKey((k) => k + 1)
                            await onCorrectedEvent()
                          }}
                        />
                      </td>
                    ) : null}
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      </div>
    </div>
  )
}
